import {
  resolveFieldValue,
  resolveUseAsAnalysisAxis,
  type FieldDef,
} from '@citizen-walk/shared-types';
import type { PostMinimal } from './usePosts';

/**
 * Phase F — old2 `apps/user/src/pages/tabs/ListTab.tsx` の sort / group logic を移植。
 *
 * 並び替えの ECW 専用オプション (Happiness / Healthy) は `ecwOnly` flag で出し分け。
 *
 * v2 差分:
 *   - PostMinimal は createdAt が Firestore Timestamp (old2 PostDoc は Date) のため `.toDate()` 経由。
 *   - extraFields 参照は `resolveFieldValue` (extraFields → top-level) で old2 getExtra を踏襲。
 *   - グループ化は「なし / 日付 / セッション」+ 地区の入力項目のうち分析軸に使う項目 (2026-10-07)。
 *     old2 の固定グループ (カテゴリ / Happiness / Healthy / 主体範囲 / 持続時間) は内部キーを決め打ちで
 *     読んでいたため廃止し、管理画面の入力項目 (分析軸) から動的に選べる形にした。
 */

export type SortKey =
  | 'newest'
  | 'oldest'
  | 'agree'
  | 'happiness-high'
  | 'happiness-low'
  | 'health-high'
  | 'health-low';

/** グループ化のキー。入力項目でまとめるときは `field:<内部キー>`。 */
export type GroupKey = 'none' | 'date' | 'session' | `field:${string}`;

const FIELD_GROUP_PREFIX = 'field:';

export const SORT_OPTIONS: Array<{ key: SortKey; label: string; ecwOnly?: boolean }> = [
  { key: 'newest', label: '新しい順' },
  { key: 'oldest', label: '古い順' },
  { key: 'agree', label: 'そう思う！が多い順' },
  { key: 'happiness-high', label: 'Happiness高い順', ecwOnly: true },
  { key: 'happiness-low', label: 'Happiness低い順', ecwOnly: true },
  { key: 'health-high', label: 'Healthy高い順', ecwOnly: true },
  { key: 'health-low', label: 'Healthy低い順', ecwOnly: true },
];

/** 入力項目に依らない固定のグループ。 */
export const GROUP_OPTIONS: Array<{ key: GroupKey; label: string }> = [
  { key: 'none', label: 'なし' },
  { key: 'date', label: '日付' },
  { key: 'session', label: 'セッション' },
];

/** グループ化に使える入力項目の型 (値が選択肢か数値で、まとめる意味があるもの)。 */
const GROUPABLE_TYPES: ReadonlySet<string> = new Set([
  'select',
  'multiselect',
  'rating-scale',
  'number',
  'checkbox',
]);

/**
 * グループ化の選択肢 = 固定 (なし / 日付 / セッション) + 地区の入力項目のうち分析軸に使う項目。
 * 分析軸は管理画面「入力項目」の「分析軸」チェック (未設定の既定は選択式・段階評価で ON)。
 * 非表示の項目は出さない。
 */
export function buildGroupOptions(postFields: FieldDef[]): Array<{ key: GroupKey; label: string }> {
  const fieldOptions = postFields
    .filter(
      (f) =>
        f.mode !== 'hidden' &&
        GROUPABLE_TYPES.has(f.type) &&
        resolveUseAsAnalysisAxis(f, 'postFields'),
    )
    .map((f) => ({ key: `${FIELD_GROUP_PREFIX}${f.key}` as GroupKey, label: f.label }));
  return [...GROUP_OPTIONS, ...fieldOptions];
}

function postMs(p: PostMinimal): number {
  return p.createdAt ? p.createdAt.toMillis() : 0;
}

function getExtra(p: PostMinimal, key: string): unknown {
  return resolveFieldValue(p as unknown as Record<string, unknown>, key);
}

export function sortPosts(posts: PostMinimal[], key: SortKey): PostMinimal[] {
  const sorted = [...posts];
  switch (key) {
    case 'newest':
      return sorted.sort((a, b) => postMs(b) - postMs(a));
    case 'oldest':
      return sorted.sort((a, b) => postMs(a) - postMs(b));
    case 'agree':
      return sorted.sort((a, b) => b.agreeCount - a.agreeCount);
    case 'happiness-high':
      return sorted.sort(
        (a, b) =>
          (Number(getExtra(b, 'HappinessScore')) || 0) -
          (Number(getExtra(a, 'HappinessScore')) || 0),
      );
    case 'happiness-low':
      return sorted.sort(
        (a, b) =>
          (Number(getExtra(a, 'HappinessScore')) || 0) -
          (Number(getExtra(b, 'HappinessScore')) || 0),
      );
    case 'health-high':
      return sorted.sort(
        (a, b) =>
          (Number(getExtra(b, 'HealthScore')) || 0) - (Number(getExtra(a, 'HealthScore')) || 0),
      );
    case 'health-low':
      return sorted.sort(
        (a, b) =>
          (Number(getExtra(a, 'HealthScore')) || 0) - (Number(getExtra(b, 'HealthScore')) || 0),
      );
    default:
      return sorted;
  }
}

export interface PostGroup {
  label: string;
  posts: PostMinimal[];
}

const UNANSWERED = '未回答';

/** 入力項目の値 → まとめる見出し (複数選択は値ごと、未入力は「未回答」)。 */
function fieldGroupLabels(value: unknown): string[] {
  if (Array.isArray(value)) {
    const vs = value.filter((v): v is string => typeof v === 'string' && v !== '');
    return vs.length > 0 ? vs : [UNANSWERED];
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return [value > 0 ? `+${value}` : String(value)];
  }
  if (typeof value === 'boolean') return [value ? 'はい' : 'いいえ'];
  if (typeof value === 'string' && value.trim() !== '') return [value];
  return [UNANSWERED];
}

/**
 * 見出しの並び順。選択式は選択肢の順、段階評価・数値は大きい順、未回答は最後。
 * 選択肢に無い値は、出てきた順で選択肢の後ろに並べる。
 */
function orderFieldGroups(labels: string[], field: FieldDef | undefined): string[] {
  const numeric = field?.type === 'rating-scale' || field?.type === 'number';
  const rank = (label: string): number => {
    if (label === UNANSWERED) return Number.MAX_SAFE_INTEGER;
    if (numeric) {
      const n = Number(label);
      return Number.isFinite(n) ? -n : Number.MAX_SAFE_INTEGER - 1;
    }
    const i = field ? field.options.findIndex((o) => o.value === label) : -1;
    return i >= 0 ? i : Number.MAX_SAFE_INTEGER - 1;
  };
  return labels
    .map((label, i) => ({ label, i }))
    .sort((x, y) => rank(x.label) - rank(y.label) || x.i - y.i)
    .map((x) => x.label);
}

export function groupPosts(
  posts: PostMinimal[],
  key: GroupKey,
  sessionMap: Map<string, string>,
  postFields: FieldDef[] = [],
): PostGroup[] {
  if (key === 'none') return [{ label: '', posts }];

  const map = new Map<string, PostMinimal[]>();
  const order: string[] = [];
  const add = (label: string, p: PostMinimal) => {
    if (!map.has(label)) {
      map.set(label, []);
      order.push(label);
    }
    map.get(label)!.push(p);
  };

  if (key.startsWith(FIELD_GROUP_PREFIX)) {
    const fieldKey = key.slice(FIELD_GROUP_PREFIX.length);
    const field = postFields.find((f) => f.key === fieldKey);
    for (const p of posts) {
      for (const label of fieldGroupLabels(getExtra(p, fieldKey))) add(label, p);
    }
    return orderFieldGroups(order, field).map((label) => ({ label, posts: map.get(label)! }));
  }

  for (const p of posts) {
    if (key === 'date') {
      add(p.createdAt ? p.createdAt.toDate().toLocaleDateString('ja-JP') : '日付なし', p);
    } else {
      add(p.sessionId ? (sessionMap.get(p.sessionId) ?? p.sessionId) : 'セッションなし', p);
    }
  }
  return order.map((label) => ({ label, posts: map.get(label)! }));
}
